import type { ResultadoAcaoVidro } from '../../didatico/manejoVidros'
import { rotuloLadoVeiculo } from '../../didatico/rotulosLado'
import { avaliarEstabilizacaoDaCena, avaliacaoDoVeiculo } from '../estabilizacao'
import { validarIntervencaoPeca } from '../intervencoesVeiculo'
import type { CenaTabletop, EstadoCortePecaTabletop, ObjetoTabletop } from '../tipos'
import type { EventoHistoricoAtendimento, RegistroOperacao, SessaoAtendimentoTabletop } from './tipos'

/**
 * Operações nos veículos durante o atendimento — as mesmas ações das
 * oficinas de corte, vidros e estabilização, feitas sobre a cópia da sessão.
 * Nada é bloqueado por "erro pedagógico" (cortar sem estabilizar, serrar o
 * vidro sem proteger a vítima): a ação acontece, fica registrada com o alerta
 * e aparece no debriefing, para o instrutor poder avaliar a decisão.
 */

/** Peças que o Table top sabe abrir, afastar ou retirar, com o rótulo usado na interface. */
export const PECAS_INTERVENCAO_TABLETOP = [
  { id: 'portaDianteiraEsquerda', nome: `Porta dianteira do ${rotuloLadoVeiculo('esquerdo')}` },
  { id: 'portaDianteiraDireita', nome: `Porta dianteira do ${rotuloLadoVeiculo('direito')}` },
  { id: 'portaTraseiraEsquerda', nome: `Porta traseira do ${rotuloLadoVeiculo('esquerdo')}` },
  { id: 'portaTraseiraDireita', nome: `Porta traseira do ${rotuloLadoVeiculo('direito')}` },
  { id: 'colunaBEsquerda', nome: `Coluna B do ${rotuloLadoVeiculo('esquerdo')}` },
  { id: 'colunaBDireita', nome: `Coluna B do ${rotuloLadoVeiculo('direito')}` },
  { id: 'teto', nome: 'Teto' },
  { id: 'tampaTraseira', nome: 'Tampa traseira' },
] as const

export function nomeDaPeca(idPeca: string): string {
  return PECAS_INTERVENCAO_TABLETOP.find((p) => p.id === idPeca)?.nome ?? idPeca
}

/** Ações possíveis por peça, na mesma linguagem do editor. */
export function acoesDaPeca(idPeca: string): ReadonlyArray<{ estado: EstadoCortePecaTabletop; rotulo: string }> {
  if (idPeca.startsWith('porta')) return [{ estado: 'aberta', rotulo: 'Abrir' }, { estado: 'removida', rotulo: 'Retirar' }]
  if (idPeca.startsWith('coluna')) return [{ estado: 'afastada', rotulo: 'Afastar' }, { estado: 'removida', rotulo: 'Remover' }]
  if (idPeca === 'teto') return [{ estado: 'afastada', rotulo: 'Rebater' }, { estado: 'removida', rotulo: 'Retirar' }]
  return [{ estado: 'aberta', rotulo: 'Abrir' }, { estado: 'removida', rotulo: 'Retirar' }]
}

const ROTULO_ESTADO_PECA: Readonly<Record<EstadoCortePecaTabletop, string>> = {
  aberta: 'aberta',
  afastada: 'afastada',
  removida: 'retirada',
}

/** Estabilização primária (suspensão anulada + rolamento travado) concluída neste veículo? */
export function veiculoEstabilizado(cena: CenaTabletop, idVeiculo: string): boolean {
  return avaliacaoDoVeiculo(avaliarEstabilizacaoDaCena(cena.objetos), idVeiculo)?.avaliacao.primariaConcluida ?? false
}

/** Frase curta da estabilização do veículo — a mesma mensagem da oficina de estabilização. */
export function situacaoEstabilizacao(cena: CenaTabletop, idVeiculo: string): { primaria: boolean; secundaria: boolean; mensagem: string } {
  const avaliacao = avaliacaoDoVeiculo(avaliarEstabilizacaoDaCena(cena.objetos), idVeiculo)?.avaliacao
  return {
    primaria: avaliacao?.primariaConcluida ?? false,
    secundaria: avaliacao?.secundariaConcluida ?? false,
    mensagem: avaliacao?.mensagem ?? 'Sem avaliação de estabilização para este objeto.',
  }
}

/**
 * Alertas de um corte ANTES de executá-lo: as mensagens da oficina de corte
 * (vidro intacto na porta/coluna) e o corte com a estabilização primária
 * incompleta. Mensagens que não são alerta ("Porta aberta.") ficam de fora.
 */
export function alertasDoCorte(
  cena: CenaTabletop,
  idVeiculo: string,
  idPeca: string,
  acao: EstadoCortePecaTabletop,
): string[] {
  const veiculo = cena.objetos.find((o) => o.id === idVeiculo)?.veiculo
  if (!veiculo) return []
  const alertas: string[] = []
  const validacao = validarIntervencaoPeca(veiculo, idPeca, acao)
  if (validacao.mensagem.startsWith('Atenção')) alertas.push(validacao.mensagem)
  if (!veiculoEstabilizado(cena, idVeiculo)) {
    alertas.push('Intervenção estrutural com a estabilização primária do veículo incompleta.')
  }
  return alertas
}

/** Alertas do manejo de vidro, a partir do que a oficina de vidros já devolve. */
export function alertasDoVidro(resultado: ResultadoAcaoVidro): string[] {
  const alertas: string[] = []
  if (!resultado.sucesso) alertas.push(resultado.mensagemDidatica)
  if (resultado.alertaClinico) alertas.push(resultado.alertaClinico)
  else if (resultado.detritosNaVitima) alertas.push('Detritos de vidro atingiram a vítima (sem proteção adequada).')
  return alertas
}

export function descricaoDoCorte(idPeca: string, acao: EstadoCortePecaTabletop): string {
  return `${nomeDaPeca(idPeca)} ${ROTULO_ESTADO_PECA[acao]}`
}

function novoId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `op-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/** Registra uma operação num veículo, com horário e alertas, e a leva para o histórico. */
export function registrarOperacao(
  sessao: SessaoAtendimentoTabletop,
  dados: Omit<RegistroOperacao, 'id' | 'tempoSimuladoSegundos'>,
  nomeVeiculo?: string,
): SessaoAtendimentoTabletop {
  const registro: RegistroOperacao = { ...dados, id: novoId(), tempoSimuladoSegundos: sessao.tempoSimuladoSegundos }
  const evento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'operacao',
    objetoVitimaId: dados.idVeiculo,
    descricao: `${nomeVeiculo ? `${nomeVeiculo}: ` : ''}${dados.descricao}`,
    ...(dados.alertas.length > 0 ? { detalhes: `Alerta: ${dados.alertas.join(' | ')}` } : {}),
  }
  return {
    ...sessao,
    operacoes: [...sessao.operacoes, registro],
    historicoEventos: [evento, ...sessao.historicoEventos],
  }
}

/** Assento → aberturas que dão acesso a quem está nele. O teto aberto libera todos. */
const ABERTURAS_POR_ASSENTO: Readonly<Record<string, readonly string[]>> = {
  motorista: ['portaDianteiraEsquerda'],
  passageiro: ['portaDianteiraDireita'],
  traseiroEsquerdo: ['portaTraseiraEsquerda'],
  traseiroDireito: ['portaTraseiraDireita'],
  traseiroCentral: ['portaTraseiraEsquerda', 'portaTraseiraDireita', 'tampaTraseira'],
}

export interface AcessoAVitima {
  readonly liberado: boolean
  /** aberturas já feitas que dão acesso a esta vítima, com o estado (ex.: "Porta dianteira esquerda retirada") */
  readonly aberturas: readonly string[]
  /** a vítima está num veículo (senão o acesso é livre) */
  readonly noVeiculo: boolean
}

/**
 * Acesso da equipe à vítima sentada: liberado quando alguma abertura
 * adjacente ao assento (ou o teto) foi aberta, afastada ou retirada. Vítima
 * fora do veículo tem acesso livre.
 */
export function acessoAVitima(cena: CenaTabletop, vitima: ObjetoTabletop): AcessoAVitima {
  const local = vitima.vitima?.local
  if (!local || local.tipo !== 'veiculo') return { liberado: true, aberturas: [], noVeiculo: false }
  const veiculo = cena.objetos.find((o) => o.id === local.idVeiculo)?.veiculo
  if (!veiculo) return { liberado: true, aberturas: [], noVeiculo: false }

  const candidatas = [...(ABERTURAS_POR_ASSENTO[local.idAncora] ?? []), 'teto']
  const aberturas = candidatas
    .map((idPeca) => {
      const estado = veiculo.estadosPecas[idPeca] as EstadoCortePecaTabletop | undefined
      return estado && estado in ROTULO_ESTADO_PECA ? descricaoDoCorte(idPeca, estado) : null
    })
    .filter((texto): texto is string => texto !== null)
  return { liberado: aberturas.length > 0, aberturas, noVeiculo: true }
}

export interface ResumoOperacoesVeiculo {
  readonly veiculo: ObjetoTabletop
  readonly operacoes: readonly RegistroOperacao[]
  readonly estabilizadoEm: number | null
  readonly primaria: boolean
  readonly secundaria: boolean
  /** pendências de decisão para o debriefing (cada uma já é uma frase) */
  readonly pendencias: readonly string[]
}

/** Resumo por veículo para o debriefing: operações, estabilização e decisões a discutir. */
export function resumoOperacoesPorVeiculo(cena: CenaTabletop, sessao: SessaoAtendimentoTabletop): ResumoOperacoesVeiculo[] {
  return cena.objetos
    .filter((o) => o.veiculo && !o.oculto)
    .map((veiculo) => {
      const operacoes = sessao.operacoes.filter((op) => op.idVeiculo === veiculo.id)
      const estabilizadoEm = operacoes.find((op) => op.tipo === 'estabilizado')?.tempoSimuladoSegundos ?? null
      const situacao = situacaoEstabilizacao(cena, veiculo.id)
      const pendencias: string[] = []
      const cortesSemEstabilizar = operacoes.filter(
        (op) => op.tipo === 'corte' && op.alertas.some((a) => a.includes('estabilização primária')),
      )
      if (cortesSemEstabilizar.length > 0) {
        pendencias.push(`${cortesSemEstabilizar.length} intervenção(ões) estrutural(is) antes de concluir a estabilização primária`)
      }
      const vidrosComAlerta = operacoes.filter((op) => op.tipo === 'vidro' && op.alertas.length > 0)
      if (vidrosComAlerta.length > 0) {
        pendencias.push(`${vidrosComAlerta.length} manejo(s) de vidro com alerta de proteção ou ferramenta`)
      }
      return { veiculo, operacoes, estabilizadoEm, primaria: situacao.primaria, secundaria: situacao.secundaria, pendencias }
    })
    .filter((r) => r.operacoes.length > 0 || r.primaria)
}
